import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiSearch, FiUser } from 'react-icons/fi';
import { toast } from 'react-toastify';
import technicianService from '../services/technicianService';
import Button from '../components/Button';
import './Technicians.css';
import '../assets/styles/ListTable.css';

const LIMIT_OPTIONS = [10, 20, 50, 100];

const Technicians = () => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [limitOption, setLimitOption] = useState('10');
  const [customLimit, setCustomLimit] = useState(25);
  const searchDebounce = useRef(null);

  const effectiveLimit = useMemo(() => {
    if (limitOption === 'custom') {
      const n = parseInt(customLimit, 10);
      return isNaN(n) ? 10 : Math.min(100, Math.max(1, n));
    }
    const n = parseInt(limitOption, 10);
    return isNaN(n) ? 10 : n;
  }, [limitOption, customLimit]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setAppliedSearch(searchTerm), 400);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, filterStatus, effectiveLimit]);

  useEffect(() => {
    fetchTechnicians();
  }, [currentPage, appliedSearch, filterStatus, effectiveLimit]);

  const fetchTechnicians = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: effectiveLimit };
      if (appliedSearch) params.search = appliedSearch;
      if (filterStatus !== 'all') params.status = filterStatus;
      const response = await technicianService.getAll(params);
      setTechnicians(response.data || []);
      setTotal(response.total ?? 0);
      setPages(response.pages ?? 1);
    } catch (error) {
      toast.error('Failed to fetch technicians');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this technician?')) {
      try {
        await technicianService.delete(id);
        toast.success('Technician deleted successfully');
        fetchTechnicians();
      } catch (error) {
        toast.error('Failed to delete technician');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      available: 'badge-success',
      on_site: 'badge-primary',
      off_duty: 'badge-gray',
      on_leave: 'badge-warning',
    };
    return `badge ${statusMap[status] || 'badge-gray'}`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Technicians</h2>
          <p className="page-subtitle">Manage your HVAC technicians</p>
        </div>
        <Link to="/technicians/new">
          <Button variant="primary" icon={<FiPlus />}>
            Add Technician
          </Button>
        </Link>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
          <option value="all">All Status</option>
          <option value="available">Available</option>
          <option value="on_site">On Site</option>
          <option value="off_duty">Off Duty</option>
          <option value="on_leave">On Leave</option>
        </select>
      </div>

      {technicians.length === 0 ? (
        <div className="empty-state">
          <FiUser className="empty-icon" />
          <h3>No Technicians Found</h3>
          <p>Get started by adding your first technician</p>
          <Link to="/technicians/new">
            <Button variant="primary" icon={<FiPlus />}>
              Add Technician
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Employee ID</th>
                  <th>Phone</th>
                  <th>Experience</th>
                  <th>Salary</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {technicians.map((tech) => (
                  <tr key={tech._id}>
                    <td>
                      <strong>{tech.name}</strong>
                      {tech.email && (
                        <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{tech.email}</div>
                      )}
                    </td>
                    <td>{tech.employeeId}</td>
                    <td>{tech.phone}</td>
                    <td>{tech.experience ?? '—'} yrs</td>
                    <td>₹{tech.salary?.toLocaleString() ?? '—'}</td>
                    <td>
                      <span className={getStatusBadge(tech.status)}>{tech.status.replace('_', ' ')}</span>
                    </td>
                    <td>
                      <div className="cell-actions">
                        <Link to={`/technicians/${tech._id}`} className="action-btn action-btn-view">
                          <FiEye /> View
                        </Link>
                        <Link to={`/technicians/${tech._id}/edit`} className="action-btn action-btn-edit">
                          <FiEdit /> Edit
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(tech._id)}
                          className="action-btn action-btn-delete"
                        >
                          <FiTrash2 /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(pages > 1 || total > technicians.length || total > 0) && (
            <div className="pagination-bar">
              <span className="pagination-info">
                Showing {(currentPage - 1) * effectiveLimit + 1}–{Math.min(currentPage * effectiveLimit, total)} of {total}
              </span>
              <div className="pagination-limit">
                <label className="pagination-limit-label">Per page</label>
                <select
                  value={limitOption}
                  onChange={(e) => setLimitOption(e.target.value)}
                  className="pagination-limit-select"
                >
                  {LIMIT_OPTIONS.map((n) => (
                    <option key={n} value={String(n)}>{n}</option>
                  ))}
                  <option value="custom">Custom</option>
                </select>
                {limitOption === 'custom' && (
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={customLimit}
                    onChange={(e) => setCustomLimit(parseInt(e.target.value, 10) || 10)}
                    className="pagination-limit-input"
                  />
                )}
              </div>
              <div className="pagination-controls">
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="pagination-info" style={{ padding: '0 0.75rem' }}>
                  Page {currentPage} of {pages}
                </span>
                <button
                  type="button"
                  className="pagination-btn"
                  onClick={() => setCurrentPage((p) => Math.min(pages, p + 1))}
                  disabled={currentPage === pages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Technicians;
